import { FC, useEffect, useState } from "react"

import { Form, Row, Card } from "antd"

import { Table } from "@/components/_Shared/Table"
import { AnalyticsBarChart } from "@/components/Analytics/AnalyticsBarChart"
import { AnalyticsLineChart } from "@/components/Analytics/AnalyticsLineChart"
import { useAnalyticsFilters } from "@/components/Analytics/AnalyticsSettings/useAnalyticsFilters"
import { Resource } from "@/constants/permission"
import { getAnalyticsPriceExcel } from "@/http/analytics"
import classes from "@/pages/Analytics/Common/Analytics.module.scss"
import AnalyticsHeader from "@/pages/Analytics/Common/AnalyticsHeader"
import AnalyticsTitle from "@/pages/Analytics/Common/AnalyticsTitle"

import { analyticsPriceColumns } from "./AnalyticsPriceColumns"

import { usePricesAnalyticsQuery } from "@/hook/Analytics/useAnalyticsQuery"
import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useDownloadFile } from "@/hook/useDownloadFile"
import { useTable } from "@/hook/useTable"

import { FilterType, IAnalyticsPageDisplayUnit, IPriceAnalytics } from "@/types/IAnalytics"
import { IDictionary, ITableColumn } from "@/types/ui"

import { formatNumber } from "@/utils/formatNumber"

const displayUnits = [{
    label: "Все",
    value: "depotId",
    bigLabel: "Все"
}]

const AnalyticsPricePage: FC = () => {
  const [form] = Form.useForm()
  const { optionsList, filterComponent, filtersComponentRef, isComparison, filterInfoAsText, getFilterInfoAsPlainText } = useAnalyticsFilters({
    baseDisplayUnits: [],
    additionalDisplayUnits: displayUnits,
    key: "AnalyticsPricePage"
  })

  const [initialColumns] = useState<ITableColumn<IPriceAnalytics>[]>(analyticsPriceColumns(true))

  const { columns, pagination, filter, order } = useTable({
    initialColumns: initialColumns
  })

  const { data: analyticsPriceData, isLoading: isLoadingAnalyticsrice } = usePricesAnalyticsQuery({ 
      optionsList 
    },
    {
      Page: pagination.page,
      PageSize: pagination.pageSize,
      Filter: filter.filter,
      OrderBy: order.orderBy
    })

  const [analyticsMapped, setAnalyticsMapped] = useState<IDictionary<number | string>[]>()
  const [analyticsTableMapped, setAnalyticsTableMapped] = useState<IPriceAnalytics[]>([])

  const [displayUnit, setDisplayUnit] = useState<IAnalyticsPageDisplayUnit>(displayUnits[0])
  const { data: depots } = useDepotSelectListQuery()

  const [uniqueFuels, setUniqueFuels] = useState<IAnalyticsPageDisplayUnit[]>([])

  const { download: excelDownload, isLoading: isExcelLoading } = useDownloadFile(() => getAnalyticsPriceExcel({
      optionsList
    })
  )

  useEffect(() => {
    // Сбросить фильтры при монтировании компонента, чтобы не было конфликтов с другими страницами
    filtersComponentRef.current?.cancelFilters()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    displayUnits.splice(1)
    displayUnits.push(...depots?.response.map(d => ({
      label: d.name,
      value: d.id.toString(),
      bigLabel: d.name
    })) ?? [])
  }, [depots])

  useEffect(() => {
    const data = analyticsPriceData?.response.data

    if (!data)
      return

    const fuelsSet = new Set<string>()

    const filtersAsText = getFilterInfoAsPlainText([FilterType.Period])

    const array = data.map((a, index) => {
      const newDictionary: IDictionary<string | number> = {}
      newDictionary["period"] = a.period
      if (isComparison && filtersAsText && filtersAsText[index]) {
          newDictionary["filterText"] = `${filtersAsText[index]}`
        }
      a.fuels?.forEach(f => {
        fuelsSet.add(f.fuelTypeName)
        newDictionary[f.fuelTypeName] = parseFloat(formatNumber(f.price))
      })

      return newDictionary
    })

    array.forEach(x => {
      if (Object.keys(x).length - 1 < fuelsSet.size){
        fuelsSet.forEach(y => {
          if(!(y in x)) {
            x[y] = 0
          }
        })
      }
    })

    setAnalyticsMapped(array)

    if (analyticsPriceData?.response.data) {
      setAnalyticsTableMapped(analyticsPriceData.response.data.map(r => ({
        rowKey: crypto.randomUUID(),
        period: r.period,
        fuels: r.fuels
      })))
    }


    setUniqueFuels(Array.from(fuelsSet.values()).sort().map(f => ({
      value: f,
      label: "",
      bigLabel: `${displayUnit.bigLabel} (${f})`
    })))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayUnit, analyticsPriceData, isComparison])

  useEffect(() => {
    const filteredDepotId = optionsList[0].depotId
    if (filteredDepotId !== null && filteredDepotId === undefined && displayUnit.label !== "Все") {
      setDisplayUnit(displayUnits[0]) // при сбросе фильтров должна поменятся и вкладка Нефтебазы
    }
  }, [optionsList, displayUnit])

  const callDepotChange = (index: number, tab: IAnalyticsPageDisplayUnit) => {
    const onDepotChangeFunction = filterComponent.props.getOnDepotChangeFunction(index)

    if (tab.label !== "Все") {
      onDepotChangeFunction.call(undefined, Number(tab.value), { value: Number(tab.value), label: tab.label }, true)
    } else {
      onDepotChangeFunction.call(undefined, undefined, [], true)
    }
  }

  const onDepotTabChange = (tab: IAnalyticsPageDisplayUnit) => {
    setDisplayUnit(tab)

    callDepotChange(1, tab)
    if (isComparison) {
      callDepotChange(2, tab)
    }
  }

  return (
    <>
      <Form form={form}>
        <AnalyticsTitle 
          text={"Динамика цен"}
        />
        <AnalyticsHeader 
          displayUnits={displayUnits}
          activeUnit={displayUnit}
          filterComponent={filterComponent}
          onChangeTabs={onDepotTabChange}
          onExcelDownload={{
            onExcelDownload: excelDownload,
            isExcelLoading: isExcelLoading
          }}
        />

        <Card bordered={false} className={ classes.cardWithTabs }> 
          <Row style={{ marginTop: "12px" }}>
            {
              !isComparison &&
                <AnalyticsLineChart data={analyticsMapped} dataKeys={uniqueFuels} filtersText={filterInfoAsText}/>
            }
            {
              isComparison &&
                <AnalyticsBarChart data={analyticsMapped} dataKeys={uniqueFuels}/>
            }
          </Row>
          <Table
            dataSource={analyticsTableMapped}
            columns={columns.data}
            isSelectable={false}
            rowKey="rowKey"
            isLoading={isLoadingAnalyticsrice}
            pagination={{
              currentPage: pagination.page,
              pageSize: pagination.pageSize,
              total: analyticsTableMapped.length,
              onPageChange: pagination.onPageChange,
              onPageSizeChange: pagination.onPageSizeChange
            }}
            onSorterChange={order.setOrderBy}
            permission={{
              resource: Resource.PricesAnalytics
            }}
          />
        </Card>
      </Form>
    </>
  )
}

export default AnalyticsPricePage
